import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import type { SlideProps } from '@/components/SlideWizard'
import { copy } from './Slide6Strangers.copy'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide6Strangers(_props: SlideProps) {
  const [storyOpen, setStoryOpen] = useState(false)
  const c = useSlideCopy(copy)
  // The panel sits at the inline end; in RTL that is the left edge, so it
  // must slide in from the left.
  const offscreen = useLocale() === 'ar' ? '-100%' : '100%'

  return (
    <div className="flex flex-col items-start text-start px-4 max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease }}
        className="border-s-4 border-red-400 ps-6"
      >
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight leading-[1.15]">
          {c.heading}
        </h2>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4, ease }}
        className="mt-8 text-xl sm:text-2xl text-slate-500 leading-relaxed"
      >
        {c.intro}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7, ease }}
        className="mt-6"
      >
        <p className="text-xl sm:text-2xl text-slate-900 leading-relaxed font-medium">
          {c.rule}
        </p>
        <button
          onClick={() => setStoryOpen(true)}
          className="mt-6 inline-flex items-center gap-2 text-base font-medium text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
        >
          {c.ctaLabel}
          <svg className="h-4 w-4 dir-flip" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
          </svg>
        </button>
      </motion.div>

      {/* Story panel */}
      <AnimatePresence>
        {storyOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setStoryOpen(false)}
              className="fixed inset-0 z-40 bg-black/30"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-white shadow-2xl overflow-y-auto"
            >
              <div className="px-6 py-8 sm:px-8">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
                    {c.storyLabel}
                  </p>
                  <button
                    onClick={() => setStoryOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>

                <h3 className="mt-6 text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight leading-tight">
                  {c.storyTitle}
                </h3>
                <p className="mt-1 text-base text-slate-400 italic">
                  {c.storySubtitle}
                </p>

                <div className="mt-8 space-y-5 text-base sm:text-[17px] text-slate-600 leading-relaxed">
                  <p>{c.storyP1}</p>
                  <p>
                    {c.storyP2Before}
                    {c.storyP2Initial}
                    {c.storyP2Middle}
                    {c.storyP2Total}
                    {c.storyP2After}
                  </p>
                  <p>{c.storyP3}</p>
                </div>

                <div className="mt-8">
                  <h4 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                    <svg className="h-5 w-5 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    {c.callout1Heading}
                  </h4>
                  <p className="mt-2 text-base sm:text-[17px] text-slate-600 leading-relaxed">
                    {c.callout1Body}
                  </p>
                </div>

                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
                  <p className="text-sm font-semibold text-slate-900">
                    {c.callout2Heading}
                  </p>
                  <p className="mt-2 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                    {c.callout2Body}
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
